import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useColorContext } from '../../context/colorContext';
import { useLanguageContext } from '../../context/languageContext';
import BubbleLandingPage from './buble_landing_page';
import BrandLogo from '../../components/ui/BrandLogo';
import { prefetchStripePlans } from '../../services/stripePlansCache';
import {
  LuBrainCircuit,
  LuMessageSquare,
  LuTarget,
  LuChevronRight,
} from 'react-icons/lu';

const toSafeHex = (hex, fallback) => {
  const value = String(hex || '').trim();
  return /^#([A-Fa-f0-9]{6})$/.test(value) ? value : fallback;
};

const hexToRgba = (hex, alpha, fallback) => {
  const safeHex = toSafeHex(hex, fallback);
  const r = parseInt(safeHex.slice(1, 3), 16);
  const g = parseInt(safeHex.slice(3, 5), 16);
  const b = parseInt(safeHex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { bgPrimary, bgSecondary, textPrimary, textSecondary, activeAccent, varkColors } = useColorContext();
  const { t } = useLanguageContext();
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    prefetchStripePlans().catch(() => {
      // Silent prefetch: pricing page will still handle errors and fallback UI.
    });
  }, []);

  const safeBgPrimary = toSafeHex(bgPrimary, '#0A0A0F');
  const safeBgSecondary = toSafeHex(bgSecondary, '#13131A');
  const safeTextPrimary = toSafeHex(textPrimary, '#FFFFFF');
  const safeTextSecondary = toSafeHex(textSecondary, '#A1A1AA');
  const safeAccent = toSafeHex(activeAccent, '#6366F1');
  const visualColor = toSafeHex(varkColors?.visual, '#7C3AED');
  const auditoryColor = toSafeHex(varkColors?.auditory, '#3B82F6');

  const containerStyle = {
    minHeight: '100vh',
    background: `linear-gradient(135deg, ${safeBgPrimary} 0%, ${hexToRgba(visualColor, 0.05, safeBgPrimary)} 20%, ${hexToRgba(auditoryColor, 0.05, safeBgPrimary)} 45%, ${hexToRgba(safeAccent, 0.06, safeBgPrimary)} 70%, ${safeBgPrimary} 100%)`,
    color: safeTextPrimary,
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
  };

  const heroStyle = {
    padding: '160px 48px 100px',
    maxWidth: 1280,
    margin: '0 auto',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 56,
  };

  const heroLeftStyle = {
    flex: '1 1 0',
    minWidth: 0,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  };

  const heroRightStyle = {
    flex: '0 0 44%',
    minWidth: 0,
    marginTop: 48,
  };

  const heroTitleStyle = {
    fontSize: 'clamp(2.4rem, 4vw, 4rem)',
    fontWeight: 800,
    lineHeight: 1.08,
    marginBottom: 24,
    color: safeTextPrimary,
    background: `linear-gradient(135deg, ${safeTextPrimary} 0%, ${hexToRgba(safeAccent, 0.9, safeAccent)} 100%)`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  };

  const heroSubtitleStyle = {
    fontSize: 'clamp(1rem, 1.6vw, 1.2rem)',
    color: safeTextPrimary,
    lineHeight: 1.65,
    marginBottom: 40,
    maxWidth: 480,
  };

  const ctaGroupStyle = {
    display: 'flex',
    gap: 16,
    justifyContent: 'center',
    marginBottom: 0,
    flexWrap: 'wrap',
  };

  const videoPlaceholderStyle = {
    borderRadius: 20,
    overflow: 'hidden',
    background: `linear-gradient(135deg, ${hexToRgba(safeBgSecondary, 0.7, safeBgSecondary)} 0%, ${hexToRgba(safeBgPrimary, 0.55, safeBgPrimary)} 100%)`,
    border: `1px solid ${hexToRgba(safeAccent, 0.16, safeAccent)}`,
    boxShadow: `0 24px 60px ${hexToRgba(safeBgPrimary, 0.28, safeBgPrimary)}`,
    boxSizing: 'border-box',
  };

  const videoFrameStyle = {
    position: 'relative',
    borderRadius: 22,
    overflow: 'hidden',
    aspectRatio: '16 / 9',
    minHeight: 200,
    background: `radial-gradient(circle at 20% 20%, ${hexToRgba(safeAccent, 0.22, safeAccent)} 0%, transparent 34%), radial-gradient(circle at 80% 30%, ${hexToRgba(safeAccent, 0.12, safeAccent)} 0%, transparent 30%), linear-gradient(160deg, ${hexToRgba(safeBgPrimary, 0.95, safeBgPrimary)} 0%, ${hexToRgba(safeBgSecondary, 0.92, safeBgSecondary)} 100%)`,
    display: 'grid',
    placeItems: 'center',
    textAlign: 'center',
    padding: 24,
  };

  const videoLabelStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 14px',
    borderRadius: 999,
    background: hexToRgba(safeAccent, 0.12, safeAccent),
    border: `1px solid ${hexToRgba(safeAccent, 0.24, safeAccent)}`,
    color: safeTextPrimary,
    fontSize: '0.82rem',
    fontWeight: 600,
    marginBottom: 16,
  };

  const videoTitleStyle = {
    fontSize: 'clamp(1.3rem, 2vw, 1.8rem)',
    fontWeight: 700,
    marginBottom: 10,
  };

  const videoTextStyle = {
    color: safeTextSecondary,
    maxWidth: 520,
    lineHeight: 1.55,
    fontSize: '0.98rem',
  };

  const primaryCtaStyle = {
    padding: '18px 52px',
    background: `linear-gradient(270deg, ${safeAccent}, #a855f7, #000000, #a855f7, ${safeAccent})`,
    backgroundSize: '300% 300%',
    animation: 'ctaGradient 5s ease infinite',
    border: 'none',
    borderRadius: 60,
    color: '#FFFFFF',
    fontSize: '1.2rem',
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'box-shadow 260ms cubic-bezier(0.22, 1, 0.36, 1), filter 220ms cubic-bezier(0.22, 1, 0.36, 1)',
    boxShadow: `0 12px 28px ${hexToRgba(safeAccent, 0.22, safeAccent)}`,
  };

  const secondaryCtaStyle = {
    padding: '14px 32px',
    background: 'transparent',
    border: `1px solid ${hexToRgba(safeAccent, 0.3, safeAccent)}`,
    borderRadius: 60,
    color: safeTextPrimary,
    fontSize: '1rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'border-color 220ms cubic-bezier(0.22, 1, 0.36, 1), background-color 220ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 260ms cubic-bezier(0.22, 1, 0.36, 1), color 220ms cubic-bezier(0.22, 1, 0.36, 1)',
  };

  const freeBadgeStyle = {
    fontSize: '0.85rem',
    color: safeTextSecondary,
  };

  const sectionStyle = {
    padding: '80px 24px',
    maxWidth: 1280,
    margin: '0 auto',
  };

  const sectionTitleStyle = {
    fontSize: 'clamp(2rem, 4vw, 2.5rem)',
    fontWeight: 700,
    textAlign: 'center',
    marginBottom: 16,
  };

  const sectionSubtitleStyle = {
    fontSize: '1.1rem',
    color: safeTextSecondary,
    textAlign: 'center',
    marginBottom: 56,
    maxWidth: 600,
    marginLeft: 'auto',
    marginRight: 'auto',
  };

  const featuresGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: 32,
  };

  const featureCardStyle = {
    padding: '32px 24px',
    background: `linear-gradient(135deg, ${hexToRgba(safeBgSecondary, 0.6, safeBgSecondary)} 0%, ${hexToRgba(safeBgPrimary, 0.4, safeBgPrimary)} 100%)`,
    border: `1px solid ${hexToRgba(safeAccent, 0.1, safeAccent)}`,
    borderRadius: 24,
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    cursor: 'pointer',
  };

  const featureIconStyle = {
    width: 48,
    height: 48,
    display: 'grid',
    placeItems: 'center',
    background: `linear-gradient(135deg, ${hexToRgba(safeAccent, 0.2, safeAccent)} 0%, ${hexToRgba(safeAccent, 0.1, safeAccent)} 100%)`,
    borderRadius: 16,
    marginBottom: 24,
    color: safeAccent,
  };

  const featureTitleStyle = {
    fontSize: '1.25rem',
    fontWeight: 600,
    marginBottom: 12,
  };

  const featureDescStyle = {
    color: safeTextSecondary,
    lineHeight: 1.5,
  };

  const faqGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(400px, 100%), 1fr))',
    gap: 24,
  };

  const faqItemStyle = {
    padding: '20px 24px',
    background: hexToRgba(safeBgSecondary, 0.4, safeBgSecondary),
    border: `1px solid ${hexToRgba(safeAccent, 0.08, safeAccent)}`,
    borderRadius: 16,
    cursor: 'pointer',
    transition: 'border-color 220ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 260ms cubic-bezier(0.22, 1, 0.36, 1), background-color 220ms cubic-bezier(0.22, 1, 0.36, 1)',
  };

  const faqQuestionStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontWeight: 600,
    fontSize: '1rem',
  };

  const faqAnswerStyle = {
    marginTop: 12,
    color: safeTextSecondary,
    fontSize: '0.9rem',
    lineHeight: 1.5,
  };

  const footerStyle = {
    padding: '48px 24px',
    textAlign: 'center',
    backgroundColor: safeBgSecondary,
    backgroundImage: `radial-gradient(circle at 10% 0%, ${hexToRgba(safeAccent, 0.11, safeAccent)} 0%, transparent 34%), linear-gradient(180deg, ${hexToRgba(safeAccent, 0.03, safeAccent)} 0%, transparent 28%, transparent 76%, ${hexToRgba(safeAccent, 0.03, safeAccent)} 100%)`,
    borderTop: `1px solid ${hexToRgba(safeAccent, 0.18, safeAccent)}`,
    color: safeTextSecondary,
    fontSize: '0.85rem',
  };

  const footerLinkStyle = {
    color: safeTextSecondary,
    textDecoration: 'none',
    fontSize: '0.9rem',
    fontWeight: 500,
    display: 'block',
    lineHeight: 1.5,
    transition: 'color 220ms cubic-bezier(0.22, 1, 0.36, 1), text-shadow 220ms cubic-bezier(0.22, 1, 0.36, 1)',
    cursor: 'pointer',
  };

  const footerGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 24,
    textAlign: 'left',
    width: '100%',
  };

  const footerSectionStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: '18px 18px 16px',
    background: hexToRgba(safeBgPrimary, 0.16, safeBgPrimary),
    border: `1px solid ${hexToRgba(safeAccent, 0.08, safeAccent)}`,
    borderRadius: 18,
  };

  const footerSectionTitleStyle = {
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.14em',
    color: safeTextPrimary,
    fontWeight: 700,
    marginBottom: 6,
  };

  const features = [
    { icon: LuBrainCircuit, title: t('landing.feature1Title'), desc: t('landing.feature1Desc') },
    { icon: LuMessageSquare, title: t('landing.feature2Title'), desc: t('landing.feature2Desc') },
    { icon: LuTarget, title: t('landing.feature3Title'), desc: t('landing.feature3Desc') },
  ];

  const faqs = [
    { question: t('landing.faq1Q'), answer: t('landing.faq1A') },
    { question: t('landing.faq2Q'), answer: t('landing.faq2A') },
    { question: t('landing.faq3Q'), answer: t('landing.faq3A') },
    { question: t('landing.faq4Q'), answer: t('landing.faq4A') },
    { question: t('landing.faq5Q'), answer: t('landing.faq5A') },
    { question: t('landing.faq6Q'), answer: t('landing.faq6A') },
    { question: t('landing.faq7Q'), answer: t('landing.faq7A') },
    { question: t('landing.faq8Q'), answer: t('landing.faq8A') },
  ];

  const scrollTo = (id) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  const revealStyle = (delay = 0) => ({
    animationName: 'landingFadeUp',
    animationDuration: '760ms',
    animationTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
    animationDelay: `${delay}ms`,
    animationFillMode: 'both',
  });


  const handleSecondaryButtonHoverIn = (event) => {
    event.currentTarget.style.borderColor = hexToRgba(safeAccent, 0.55, safeAccent);
    event.currentTarget.style.color = safeTextPrimary;
    event.currentTarget.style.background = hexToRgba(safeAccent, 0.09, safeAccent);
    event.currentTarget.style.boxShadow = `0 10px 24px ${hexToRgba(safeAccent, 0.12, safeAccent)}`;
  };

  const handleSecondaryButtonHoverOut = (event) => {
    event.currentTarget.style.borderColor = hexToRgba(safeAccent, 0.3, safeAccent);
    event.currentTarget.style.color = safeTextPrimary;
    event.currentTarget.style.background = 'transparent';
    event.currentTarget.style.boxShadow = 'none';
  };

  const handleFaqHoverIn = (event) => {
    event.currentTarget.style.backgroundColor = hexToRgba(safeBgSecondary, 0.58, safeBgSecondary);
    event.currentTarget.style.borderColor = hexToRgba(safeAccent, 0.3, safeAccent);
    event.currentTarget.style.boxShadow = `0 12px 28px ${hexToRgba(safeBgPrimary, 0.24, safeBgPrimary)}`;
  };

  const handleFaqHoverOut = (event) => {
    event.currentTarget.style.backgroundColor = hexToRgba(safeBgSecondary, 0.4, safeBgSecondary);
    event.currentTarget.style.borderColor = hexToRgba(safeAccent, 0.08, safeAccent);
    event.currentTarget.style.boxShadow = 'none';
  };

  const handleFooterLinkHoverIn = (event) => {
    event.currentTarget.style.color = safeTextPrimary;
    event.currentTarget.style.textShadow = `0 0 18px ${hexToRgba(safeAccent, 0.22, safeAccent)}`;
  };

  const handleFooterLinkHoverOut = (event) => {
    event.currentTarget.style.color = safeTextSecondary;
    event.currentTarget.style.textShadow = 'none';
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Plynote',
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'Web',
    description: 'AI note agent that transforms scientific PDFs into mindmaps, flashcards, equations and audio summaries.',
    url: 'https://www.plynote.io',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR' },
    provider: { '@type': 'Organization', name: 'Plynote', url: 'https://www.plynote.io' },
  };

  return (
    <>
    <Helmet>
      <title>Plynote — AI Note Agent for Scientific Documents</title>
      <meta name="description" content="Transform your scientific PDFs into mindmaps, flashcards, equations and audio summaries with Plynote's AI note agent. Study smarter, not harder." />
      <link rel="canonical" href="https://www.plynote.io/" />
      <meta property="og:title" content="Plynote — AI Note Agent for Scientific Documents" />
      <meta property="og:description" content="Transform your scientific PDFs into mindmaps, flashcards, equations and audio summaries with Plynote's AI note agent. Study smarter, not harder." />
      <meta property="og:url" content="https://www.plynote.io/" />
      <meta property="og:type" content="website" />
      <meta name="twitter:title" content="Plynote — AI Note Agent for Scientific Documents" />
      <meta name="twitter:description" content="Transform your scientific PDFs into mindmaps, flashcards, equations and audio summaries with Plynote's AI note agent. Study smarter, not harder." />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
    <div style={containerStyle}>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:opsz,wght@14..32,300;14..32,400;14..32,500;14..32,600;14..32,700;14..32,800&display=swap');

          @keyframes ctaGradient {
            0%   { background-position: 0% 50%; }
            50%  { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }

          @keyframes landingFadeUp {
            0% {
              opacity: 0;
              transform: translate3d(0, 14px, 0) scale(0.996);
            }
            100% {
              opacity: 1;
              transform: translate3d(0, 0, 0) scale(1);
            }
          }

          @supports not (animation-name: landingFadeUp) {
            * { animation: none !important; }
          }
          
          body {
            font-family: 'Inter', sans-serif;
            overflow-x: hidden;
          }
          
          ::-webkit-scrollbar {
            width: 8px;
          }
          
          ::-webkit-scrollbar-track {
            background: ${hexToRgba(safeBgSecondary, 0.5, safeBgSecondary)};
          }
          
          ::-webkit-scrollbar-thumb {
            background: ${hexToRgba(safeAccent, 0.5, safeAccent)};
            border-radius: 4px;
          }
          
          ::-webkit-scrollbar-thumb:hover {
            background: ${hexToRgba(safeAccent, 0.7, safeAccent)};
          }

          /* Video placeholder responsiveness */
          .landing-video-placeholder {
            width: min(720px, 100%);
            max-width: 720px;
            margin: 0 auto 28px;
            box-sizing: border-box;
            background: transparent;
          }

          .landing-video-placeholder video {
            width: 100%;
            height: auto;
            display: block;
          }

          @media (max-width: 960px) {
            .landing-hero {
              flex-direction: column !important;
              padding: 120px 24px 64px !important;
              gap: 40px !important;
            }
            .landing-hero-left {
              text-align: center !important;
            }
            .landing-hero-left p {
              max-width: 100% !important;
              margin-left: auto !important;
              margin-right: auto !important;
            }
            .landing-hero-left > div {
              justify-content: center !important;
            }
            .landing-hero-right {
              width: 100% !important;
              flex: none !important;
              max-width: 600px !important;
              margin: 0 auto !important;
            }
            .landing-section { padding: 56px 20px !important; }
          }

          @media (max-width: 640px) {
            .landing-hero { padding: 104px 16px 48px !important; }
            .landing-section { padding: 40px 16px !important; }
            .landing-video-placeholder { border-radius: 14px !important; }
          }

          @media (max-width: 480px) {
            .landing-hero { padding: 88px 12px 40px !important; }
            .landing-section { padding: 28px 12px !important; }
          }
        `}
      </style>

      <BubbleLandingPage
        current="home"
        onFeaturesClick={() => scrollTo('features')}
        onPricingClick={() => navigate('/connection')}
        onHelpClick={() => scrollTo('help')}
        onCtaClick={() => navigate('/connection')}
        onLogoClick={() => navigate('/')}
        ctaLabel={t('landing.ctaStart')}
      />

      {/* Hero */}
      <section className="landing-hero" style={{ ...heroStyle, ...revealStyle(120) }}>

        {/* Left: text + CTA */}
        <div className="landing-hero-left" style={heroLeftStyle}>
          <h1 style={heroTitleStyle}>{t('landing.heroTitle')}</h1>
          <p style={heroSubtitleStyle}>
            {t('landing.heroSubtitle')}
          </p>
          <div style={ctaGroupStyle}>
            <button
              style={primaryCtaStyle}
              onClick={() => navigate('/connection')}
              onMouseEnter={(event) => {
                event.currentTarget.style.filter = 'brightness(1.06) saturate(1.03)';
                event.currentTarget.style.boxShadow = `0 16px 34px ${hexToRgba(safeAccent, 0.3, safeAccent)}`;
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.filter = 'none';
                event.currentTarget.style.boxShadow = `0 12px 28px ${hexToRgba(safeAccent, 0.22, safeAccent)}`;
              }}
            >
              {t('landing.startFree')}
            </button>
          </div>
        </div>

        {/* Right: video + badge */}
        <div className="landing-hero-right" style={heroRightStyle}>
          <div className="landing-video-placeholder" style={videoPlaceholderStyle}>
            <video
              className="landing-video"
              style={{ width: '100%', height: 'auto', display: 'block' }}
              controls
              loop
              autoPlay
              muted
              playsInline
            >
              <source src="/landing_page_video.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          <div style={{ ...freeBadgeStyle, textAlign: 'center', marginTop: 14 }}>
            {t('landing.noCreditCard')}
          </div>
        </div>

      </section>

      {/* Features */}
      <section id="features" className="landing-section" style={{ ...sectionStyle, ...revealStyle(180) }}>
        <h2 style={sectionTitleStyle}>{t('landing.featuresTitle')}</h2>
        <p style={sectionSubtitleStyle}>
          {t('landing.featuresSubtitle')}
        </p>
        <div style={featuresGridStyle}>
          {features.map((feature, index) => (
            <div
              key={index}
              style={{ ...featureCardStyle, ...revealStyle(220 + index * 90) }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = `0 20px 40px ${hexToRgba('#000000', 0.3, '#000000')}`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={featureIconStyle}>
                <feature.icon size={24} />
              </div>
              <h3 style={featureTitleStyle}>{feature.title}</h3>
              <p style={featureDescStyle}>{feature.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section
        style={{
          maxWidth: 860,
          margin: '0 auto 24px',
          padding: '28px 26px',
          borderRadius: 28,
          background: `linear-gradient(135deg, ${hexToRgba(safeBgSecondary, 0.6, safeBgSecondary)} 0%, ${hexToRgba(safeBgPrimary, 0.4, safeBgPrimary)} 100%)`,
          border: `1px solid ${hexToRgba(safeAccent, 0.16, safeAccent)}`,
          boxShadow: `0 20px 48px ${hexToRgba(safeBgPrimary, 0.24, safeBgPrimary)}`,
          textAlign: 'center',
        }}
      >
        
        <h2
          style={{
            ...sectionTitleStyle,
            marginBottom: 14,
            maxWidth: 760,
            marginLeft: 'auto',
            marginRight: 'auto',
            background: `linear-gradient(135deg, ${safeTextPrimary} 0%, ${hexToRgba(safeAccent, 0.9, safeAccent)} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          {t('landing.notJustTool1')}
          <br />
          {t('landing.notJustTool2')}
        </h2>
        <p
          style={{
            fontSize: '1.05rem',
            lineHeight: 1.7,
            color: safeTextSecondary,
            maxWidth: 720,
            margin: '0 auto',
          }}
        >
          {t('landing.notJustToolDesc')}
        </p>
        <div style={{ marginTop: 20 }}>
          <button
            type="button"
            style={primaryCtaStyle}
            onClick={() => navigate('/connection')}
            onMouseEnter={(event) => {
              event.currentTarget.style.filter = 'brightness(1.06) saturate(1.03)';
              event.currentTarget.style.boxShadow = `0 16px 34px ${hexToRgba(safeAccent, 0.3, safeAccent)}`;
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.filter = 'none';
              event.currentTarget.style.boxShadow = `0 12px 28px ${hexToRgba(safeAccent, 0.22, safeAccent)}`;
            }}
          >
            {t('landing.startFree')}
          </button>
        </div>
      </section>

      {/* Why Plynote is better */}
      <section className="landing-section" style={{ ...sectionStyle, ...revealStyle(340) }}>
        <h2 style={sectionTitleStyle}>{t('landing.whyTitle')}</h2>
        <p style={sectionSubtitleStyle}>
          {t('landing.whySubtitle')}
        </p>
        <div style={{
          display: 'flex',
          gap: 20,
          justifyContent: 'center',
          flexWrap: 'wrap',
          maxWidth: 1000,
          margin: '0 auto',
        }}>
          <button
            type="button"
            style={secondaryCtaStyle}
            onClick={() => navigate('/comparative/plynote-vs-notebooklm')}
            onMouseEnter={handleSecondaryButtonHoverIn}
            onMouseLeave={handleSecondaryButtonHoverOut}
          >
            Plynote vs NotebookLM
          </button>
          <button
            type="button"
            style={secondaryCtaStyle}
            onClick={() => navigate('/comparative/plynote-vs-turbo-ai')}
            onMouseEnter={handleSecondaryButtonHoverIn}
            onMouseLeave={handleSecondaryButtonHoverOut}
          >
            Plynote vs Turbo AI
          </button>
          <button
            type="button"
            style={secondaryCtaStyle}
            onClick={() => navigate('/comparative/plynote-vs-studley')}
            onMouseEnter={handleSecondaryButtonHoverIn}
            onMouseLeave={handleSecondaryButtonHoverOut}
          >
            Plynote vs Studley
          </button>
        </div>
      </section>

      {/* Help / FAQ */}
      <section id="help" className="landing-section" style={{ ...sectionStyle, ...revealStyle(380) }}>
        <h2 style={sectionTitleStyle}>{t('landing.faqTitle')}</h2>
        <p style={sectionSubtitleStyle}>
          {t('landing.faqSubtitle')}
        </p>
        <div style={faqGridStyle}>
          {faqs.map((faq, index) => (
            <div
              key={index}
              style={{ ...faqItemStyle, ...revealStyle(300 + index * 80) }}
              onClick={() => setOpenFaq(openFaq === index ? null : index)}
              onMouseEnter={handleFaqHoverIn}
              onMouseLeave={handleFaqHoverOut}
            >
              <div style={faqQuestionStyle}>
                <span>{faq.question}</span>
                <LuChevronRight
                  size={18}
                  style={{
                    transition: 'transform 0.2s ease',
                    transform: openFaq === index ? 'rotate(90deg)' : 'rotate(0deg)',
                  }}
                />
              </div>
              {openFaq === index && (
                <div style={faqAnswerStyle}>
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer style={{ ...footerStyle, ...revealStyle(420) }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div className="footer-layout" style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 240px) minmax(0, 1fr)', alignItems: 'start', gap: 24, marginBottom: 32 }}>
            <BrandLogo size={20} />
            <div style={footerGridStyle}>
              <div style={footerSectionStyle}>
                <div style={footerSectionTitleStyle}>{t('landing.footerProduct')}</div>
                <a style={footerLinkStyle} onClick={() => scrollTo('features')} onMouseEnter={handleFooterLinkHoverIn} onMouseLeave={handleFooterLinkHoverOut}>{t('landing.footerFeatures')}</a>
                <a style={footerLinkStyle} onClick={() => scrollTo('help')} onMouseEnter={handleFooterLinkHoverIn} onMouseLeave={handleFooterLinkHoverOut}>{t('landing.footerHelp')}</a>
              </div>

              <div style={footerSectionStyle}>
                <div style={footerSectionTitleStyle}>{t('landing.footerLegal')}</div>
                <a style={footerLinkStyle} onClick={() => navigate('/privacy-policy')} onMouseEnter={handleFooterLinkHoverIn} onMouseLeave={handleFooterLinkHoverOut}>{t('landing.footerPrivacy')}</a>
                <a style={footerLinkStyle} onClick={() => navigate('/terms-of-service')} onMouseEnter={handleFooterLinkHoverIn} onMouseLeave={handleFooterLinkHoverOut}>{t('landing.footerTerms')}</a>
                <a style={footerLinkStyle} onClick={() => navigate('/support')} onMouseEnter={handleFooterLinkHoverIn} onMouseLeave={handleFooterLinkHoverOut}>{t('landing.footerSupport')}</a>
              </div>

              <div style={footerSectionStyle}>
                <div style={footerSectionTitleStyle}>{t('landing.footerComparative')}</div>
                <a style={footerLinkStyle} onClick={() => navigate('/comparative/plynote-vs-notebooklm')} onMouseEnter={handleFooterLinkHoverIn} onMouseLeave={handleFooterLinkHoverOut}>Plynote vs NotebookLM</a>
                <a style={footerLinkStyle} onClick={() => navigate('/comparative/plynote-vs-turbo-ai')} onMouseEnter={handleFooterLinkHoverIn} onMouseLeave={handleFooterLinkHoverOut}>Plynote vs Turbo AI</a>
                <a style={footerLinkStyle} onClick={() => navigate('/comparative/plynote-vs-studley')} onMouseEnter={handleFooterLinkHoverIn} onMouseLeave={handleFooterLinkHoverOut}>Plynote vs Studley</a>
              </div>
            </div>
          </div>
          <div>© 2026 Plynote.</div>
        </div>
      </footer>

      <style>
        {`
          @media (max-width: 860px) {
            .footer-layout {
              grid-template-columns: 1fr !important;
            }
          }

          @media (max-width: 720px) {
            .footer-layout {
              gap: 18px !important;
            }
          }
        `}
      </style>
    </div>
    </>
  );
};

export default LandingPage;